import { PropsWithChildren } from 'react'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import EllipseIcon from 'shared/icons/ellipse-icon'
import { UserInfoPopover } from 'modules/community/components/user-info-popover/user-info-popover'
import { getReadableMemberWorkspaceRole } from 'modules/community/enums/member-enum'
import { PostAuthorInterface } from 'modules/community/types/post-interface'

interface AuthorInfoProps extends PropsWithChildren {
  author?: PostAuthorInterface
  userId: number
  highlightedAuthorHTML?: string
}

export const PostAuthorInfo = ({
  author,
  userId,
  children,
  highlightedAuthorHTML,
}: AuthorInfoProps) => {
  const { t } = useTranslation()

  return children ? (
    <p className="inline-flex h-fit items-start gap-2.5 text-text-secondary">
      <UserInfoPopover userId={userId} className="shrink-0">
        <img
          src={author?.avatarUrl || '/default_post_author_avatar.png'}
          alt={author?.displayName}
          className="size-10 rounded-full"
          width={40}
          height={40}
        />
      </UserInfoPopover>
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-1">
          <span
            className="max-h-[20px] max-w-[150px] truncate text-text-primary md:max-w-sm"
            dangerouslySetInnerHTML={{
              __html: highlightedAuthorHTML
                ? DOMPurify.sanitize(highlightedAuthorHTML)
                : author?.displayName || '',
            }}
          />
          <EllipseIcon />
          {author?.workspaceRole && (
            <span className="self-start rounded border border-transparent bg-brand-primary/[0.07] px-[5px] text-[10px] leading-5 text-brand-primary/[1]">
              {t(getReadableMemberWorkspaceRole(author.workspaceRole))}
            </span>
          )}
        </div>
        <div className="mt-auto">{children}</div>
      </div>
    </p>
  ) : (
    <p className="inline-flex items-center gap-2.5 text-text-secondary">
      <UserInfoPopover userId={userId}>
        <img
          src={author?.avatarUrl || '/default_post_author_avatar.png'}
          alt={author?.displayName}
          className="size-10 rounded-full"
          width={40}
          height={40}
        />
      </UserInfoPopover>
      <div className="flex flex-col items-center">
        <span
          className="max-w-[250px] truncate text-text-primary md:max-w-sm"
          dangerouslySetInnerHTML={{
            __html: highlightedAuthorHTML
              ? DOMPurify.sanitize(highlightedAuthorHTML)
              : author?.displayName || '',
          }}
        />
        {author?.workspaceRole && (
          <span className="self-start rounded border border-transparent bg-brand-primary/[0.07] px-[5px] text-[10px] leading-5 text-brand-primary/[1]">
            {t(getReadableMemberWorkspaceRole(author.workspaceRole))}
          </span>
        )}
      </div>
    </p>
  )
}
